/**
 * Claude of Iron — browser entry point.
 *
 * Responsibilities, and nothing else:
 *   1. create the shared context
 *   2. register systems (order is irrelevant; the engine topologically sorts them)
 *   3. init, prewarm, install the tooling hook, start the loop
 *   4. wire resize and surface boot failures visibly rather than to the console alone
 *
 * No gameplay logic lives here. See ARCHITECTURE.md §1 and §3.1.
 */

import './dev/dev.css';

import { createContext, Engine, DEFAULT_SEED } from './core/index.js';
import { InputSystem } from './input/index.js';
import { PhysicsSystem } from './physics/index.js';
import { CombatSystem } from './combat/index.js';
import { AiSystem } from './ai/index.js';
import { RenderSystem } from './render/index.js';
import { MaterialsSystem } from './materials/index.js';
import { AnimationSystem } from './animation/index.js';
import { FighterVisualSystem } from './fighters/index.js';
import { WorldSystem } from './world/index.js';
import { ImpactFxSystem } from './fx/index.js';
import { AudioSystem } from './audio/index.js';
import { UiSystem } from './ui/index.js';
import { DebugOverlay, installTestHooks } from './dev/index.js';

/** Reads boot options from the query string so tools can configure a page without a UI. */
function readBootOptions() {
  const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
  const deterministic = params.get('deterministic') === '1';
  const capture = params.get('capture') === '1' || deterministic;
  const num = (key, fallback) => {
    const v = params.get(key);
    if (v === null) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  return {
    seed: params.has('seed') ? num('seed', DEFAULT_SEED) >>> 0 : DEFAULT_SEED,
    dev: params.get('dev') === '1' || params.get('dev') === 'true',
    deterministic,
    capture,
    preset: params.get('preset') || null,
    hitboxes: params.get('hitboxes') === '1',
    p0: params.get('p0') || 'kestrel',
    p1: params.get('p1') || 'bulwark',
    screen: params.get('screen') || (capture ? 'fight' : 'title'),
    autostart: params.get('autostart') !== '0',
    width: params.has('w') ? num('w', 0) : 0,
    height: params.has('h') ? num('h', 0) : 0,
  };
}

function showBootStatus(message, sub) {
  if (typeof document === 'undefined') return null;
  let el = document.querySelector('.boot-status');
  if (!el) {
    el = document.createElement('div');
    el.className = 'boot-status';
    el.innerHTML = '<h1>Claude of Iron</h1><div class="boot-sub"></div>';
    document.body.appendChild(el);
  }
  const subEl = el.querySelector('.boot-sub');
  if (subEl) subEl.textContent = sub || message || '';
  return el;
}

function showBootError(err) {
  const el = showBootStatus('', 'failed to start');
  if (!el) return;
  const pre = document.createElement('div');
  pre.className = 'boot-error';
  const detail = err && err.stack ? err.stack : String(err);
  pre.textContent = detail;
  el.appendChild(pre);
}

function clearBootStatus() {
  if (typeof document === 'undefined') return;
  const el = document.querySelector('.boot-status');
  if (el) el.remove();
}

async function boot() {
  const opts = readBootOptions();
  showBootStatus('', 'starting engine');

  const canvas = document.getElementById('stage');
  if (!canvas) throw new Error('index.html is missing <canvas id="stage">');

  const configOverrides = {};
  if (opts.preset) configOverrides.preset = opts.preset;
  if (opts.hitboxes) configOverrides.showHitboxes = true;
  if (opts.deterministic) configOverrides.dynamicResolution = false;

  const ctx = createContext({
    seed: opts.seed,
    dev: opts.dev || opts.deterministic,
    deterministic: false, // the loop starts live; tools switch this on through the hook
    canvas,
    configOverrides,
    logLevel: opts.dev ? 'debug' : 'warn',
  });
  ctx.app.selected = [opts.p0, opts.p1];
  ctx.app.mode = 'vs-ai';
  // Tooling pages enter the requested deterministic scene immediately. A normal visit always
  // begins at the title so the player gets the complete one-player journey.
  ctx.app.screen = opts.screen;
  ctx.flags.capture = opts.capture;

  // Size the backing store before any system initialises, so shadow frusta and the camera aspect
  // are correct on the very first frame.
  //
  // The canvas's own laid-out size is the source of truth, NOT window.innerWidth: an embedded or
  // not-yet-laid-out frame reports 0 there, and a renderer handed a 0x0 target draws nothing while
  // reporting no error at all. Fall back through the window to a sane default so the game can
  // never boot into an invisible zero-sized viewport.
  const dpr = opts.deterministic ? 1 : Math.min(window.devicePixelRatio || 1, 2);
  const measure = () => ({
    width: opts.width || canvas.clientWidth || window.innerWidth || 1280,
    height: opts.height || canvas.clientHeight || window.innerHeight || 720,
  });
  const first = measure();
  ctx.render.width = first.width;
  ctx.render.height = first.height;
  ctx.render.dpr = dpr;

  const engine = new Engine(ctx);
  engine.register(
    InputSystem,
    PhysicsSystem,
    CombatSystem,
    AiSystem,
    RenderSystem,
    MaterialsSystem,
    AnimationSystem,
    FighterVisualSystem,
    WorldSystem,
    ImpactFxSystem,
    AudioSystem,
    UiSystem,
    DebugOverlay,
  );

  showBootStatus('', 'initialising systems');
  await engine.init();

  // Shader prewarm runs AFTER every system has initialised, so materials added by systems that
  // init later than `render` (the collision overlay today; fx, world and fighters later) are
  // included. Compiling inside render.init() missed them and produced a stall the first time the
  // overlay was enabled — ARCHITECTURE.md §11.3 requires zero in-combat compilation.
  showBootStatus('', 'compiling shaders');
  const renderSystem = ctx.systems.get('render');
  if (renderSystem) await renderSystem.prewarm(ctx);

  // Resolve `ready` only after init AND the first presented frame, so a tool that awaits it can
  // capture immediately without racing shader compilation.
  let resolveReady;
  const ready = new Promise((res) => { resolveReady = res; });
  installTestHooks(ctx, engine, ready);

  engine.resize(ctx.render.width, ctx.render.height, ctx.render.dpr);

  let lastW = ctx.render.width;
  let lastH = ctx.render.height;
  const onResize = () => {
    if (opts.width || opts.height) return; // a tool pinned the viewport; do not fight it
    const { width, height } = measure();
    const nextDpr = Math.min(window.devicePixelRatio || 1, 2);
    if (width === lastW && height === lastH && nextDpr === ctx.render.dpr) return;
    lastW = width;
    lastH = height;
    engine.resize(width, height, nextDpr);
  };

  // A ResizeObserver on the canvas catches every layout change, including the FIRST one — which
  // a `window.resize` listener misses entirely when the page boots before layout settles.
  let observer = null;
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(onResize);
    observer.observe(canvas);
  }
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  // Present one frame before starting the loop so the first visible frame is already correct.
  engine.present();
  clearBootStatus();

  if (opts.autostart && !opts.deterministic) engine.start();
  resolveReady();

  ctx.log.info(
    `Claude of Iron ready — seed 0x${ctx.seed.toString(16)}, preset ${ctx.render.preset}, ` +
      `${ctx.render.width}x${ctx.render.height} @${ctx.render.dpr}`,
  );

  // Surfaced for manual debugging in the console; tools use window.__COI__.api.
  window.addEventListener('beforeunload', () => {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
    if (observer) observer.disconnect();
    engine.dispose();
  });

  return { ctx, engine };
}

boot().catch((err) => {
  // A boot failure must be visible on screen. A blank canvas with a console error is the single
  // most common way a WebGL project appears to "work" while being completely broken.
  console.error('[coi] boot failed', err);
  showBootError(err);
  if (typeof window !== 'undefined') window.__COI_BOOT_ERROR__ = String(err && err.stack || err);
});
